import { Box, Button, Typography } from '@mui/material'
import { makeStyles } from '@mui/styles'
import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import '../../../../assets/styles/lkqAdminUI.less'
import ListAttachment, { IEmailAttachment } from '../components/ListAttachment'
import { useDispatch, useSelector } from 'react-redux'
import moment from 'moment'
import {
  addEmailAttachment,
  deleteEmailAttachment,
  downloadEmailAttachment,
  editEmailAttachment,
  getEmailAttachList
} from '../state/actions'
import AddAttachment from '../components/AddAttachment'
import DeleteAttachment from '../components/DeleteAttachment'
import { useNotifier } from '@cbgms/base/notifier'
import { IRootReducerState } from 'state/reducers'
import AddIcon from '@mui/icons-material/Add'

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '10px'
  },
  leftBox: {
    flex: 1
  },
  rightBox: {
    flex: '0 0 auto',
    [theme.breakpoints.up('sm')]: {
      marginRight: '33.75px'
    },
    [theme.breakpoints.up('md')]: {
      marginRight: '67.5px'
    },
    [theme.breakpoints.up('lg')]: {
      marginRight: '135px'
    },
    [theme.breakpoints.up('xl')]: {
      marginRight: '195px'
    }
  },
  title: {
    fontSize: '1.25rem'
  },
  addButton: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:hover': {
      backgroundColor: theme.palette.primary.dark
    }
  },
  addIcon: {
    fontWeight: 900
  },
  addText: {
    fontWeight: 900
  }
}))

const EmailAttachment = () => {
  const classes = useStyles()
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const notifier = useNotifier()
  const userData = useSelector((state: IRootReducerState) => state.auth.user)

  const [isLoading, setIsLoading] = useState(false)
  const [list, setList] = useState<IEmailAttachment[]>([])
  const [openAddAttachment, setOpenAddAttachment] = useState(false)
  const [openDeleteAttachment, setOpenDeleteAttachment] = useState(false)
  const [isEditingAttachment, setIsEditingAttachment] = useState(false)
  const [editingAttachment, setEditingAttachment] = useState<IEmailAttachment | null>(null)
  const [deletingAttachment, setDeletingAttachement] = useState<IEmailAttachment | null>(null)

  const App_Id = useSelector((state: IRootReducerState) => state.auth.user.AppId)
  const fetchEmailAttchList = useCallback(async () => {
    setIsLoading(true)
    try {
      const { Data }: { Data: IEmailAttachment[] } = (await dispatch(getEmailAttachList(App_Id as string))) as { Data: IEmailAttachment[] }
      setList(Data.sort((a: IEmailAttachment, b: IEmailAttachment) => moment(b.UpdatedAt).diff(a.UpdatedAt)))
    } catch (err) {
      console.error(err)
    } finally {
      setIsLoading(false)
    }
  }, [])

  const addEmailAttachmentFn = useCallback(async (attachmentFile: File, attachmentDesc: string) => {
    setIsLoading(true)
    try {
      const { Data }: { Data: unknown } = await dispatch(
        addEmailAttachment(Number(App_Id), attachmentDesc, userData.family_name, userData.family_name, attachmentFile)
      )
      const data = Data as { Status: string; Message: string }

      if (data.Status === 'Success') notifier.success(t('email_attachment.add_attch_success'))
    } catch (err) {
      console.error(err)
      notifier.error(t('email_attachment.add_attch_failed'))
    } finally {
      setIsLoading(false)
      setOpenAddAttachment(false)
    }
  }, [])

  const editEmailAttachmentFn = useCallback(async (attachmentDesc: string, attachmentUUID: string, attachmentFile?: File) => {
    setIsLoading(true)
    try {
      const { Data }: { Data: unknown } = await dispatch(
        editEmailAttachment(attachmentDesc, attachmentUUID, userData.family_name, attachmentFile)
      )
      const data = Data as { Status: string; Message: string }

      if (data.Status === 'Success') notifier.success(t('email_attachment.edit_attch_success'))
    } catch (err) {
      console.error(err)
      notifier.error(t('email_attachment.edit_attch_failed'))
    } finally {
      setIsLoading(false)
      setOpenAddAttachment(false)
    }
  }, [])

  const deleteEmailAttachmentFn = useCallback(async (AttachmentUUID: string) => {
    setIsLoading(true)
    try {
      const { Data }: { Data: unknown } = await dispatch(deleteEmailAttachment(AttachmentUUID))
      const data = Data as { Status: string; Message: string }

      if (data.Status === 'Success') notifier.success(t('email_attachment.delete_attch_success'))
    } catch (err: unknown) {
      const { response } = err as {
        response: {
          data: {
            Error: string
          }
        }
      }

      if (response.data.Error === 'validation_failed') {
        notifier.error(t('email_attachment.delete_attch_validation_failed'))
      } else notifier.error(t('email_attachment.delete_attch_failed'))
    } finally {
      setIsLoading(false)
      setDeletingAttachement(null)
      setOpenDeleteAttachment(false)
    }
  }, [])

  const downloadEmailAttachmentFn = useCallback(async (AttachmentUUID: string, fileName: string) => {
    setIsLoading(true)
    try {
      const response: unknown = await dispatch(downloadEmailAttachment(AttachmentUUID))

      const url = window.URL.createObjectURL(new Blob([response as Blob]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', fileName)
      document.body.appendChild(link)
      link.click()
      link.remove()

      notifier.success(t('email_attachment.download_attch_success'))
    } catch (err) {
      console.error(err)
      notifier.error(t('email_attachment.download_attch_failed'))
    } finally {
      setIsLoading(false)
    }
  }, [])

  const handleDeleteAttachment = async (AttachmentUUID: string) => {
    await deleteEmailAttachmentFn(AttachmentUUID)
    await fetchEmailAttchList()
  }

  const handleAddEditAttachment = async (attachmentDesc: string, attachmentFile?: File, isEditing = false) => {
    if (!isEditing) await addEmailAttachmentFn(attachmentFile as File, attachmentDesc)
    else {
      await editEmailAttachmentFn(attachmentDesc, editingAttachment?.AttachmentUUID as string, attachmentFile)
    }

    await fetchEmailAttchList()
  }

  const handleOpenAddAttachment = useCallback(
    (open: boolean) => {
      setIsEditingAttachment(false)
      setEditingAttachment(null)
      setOpenAddAttachment(open)
    },
    [open]
  )

  const setEditingAttachmentDialogOpen = useCallback((editingAttachment: IEmailAttachment) => {
    setIsEditingAttachment(true)
    setEditingAttachment(editingAttachment)
    setOpenAddAttachment(true)
  }, [])

  const setDeletingAttachmentDialogOpen = useCallback((deletingAttachment: IEmailAttachment) => {
    setDeletingAttachement(deletingAttachment)
    setOpenDeleteAttachment(true)
  }, [])

  useEffect(() => {
    fetchEmailAttchList()
  }, [])

  return (
    <>
      <Box className={classes.container}>
        <Box className={classes.leftBox}>
          <Typography variant='h6' className={classes.title}>
            {t('Customers.EmailAttachment')}
          </Typography>
        </Box>
        <Box className={classes.rightBox} style={{ display: 'flex', gap: '10px' }}>
          <Link to='/LKQ'>
            <Button variant='outlined' color='inherit' className='ui tiny ml-half' style={{ height: '35px', marginBottom: '1px' }}>
              <i className='arrow left icon' style={{ marginTop: '-10px' }} />
              {t('module.Back')}
            </Button>
          </Link>
          <Button
            type='submit'
            className={classes.addButton}
            startIcon={<AddIcon className={classes.addIcon} />}
            onClick={() => setOpenAddAttachment(true)}
          >
            <span className={classes.addText}>{t('email_attachment.Add')}</span>
          </Button>
        </Box>
      </Box>

      <ListAttachment
        list={list}
        isLoading={isLoading}
        setEditingAttachmentDialogOpen={setEditingAttachmentDialogOpen}
        setDeletingAttachmentDialogOpen={setDeletingAttachmentDialogOpen}
        downloadEmailAttachment={downloadEmailAttachmentFn}
      />
      <AddAttachment
        open={openAddAttachment}
        onCancel={() => handleOpenAddAttachment(false)}
        isEditing={isEditingAttachment}
        editingAttachment={editingAttachment}
        onConfirm={handleAddEditAttachment}
        isLoading={isLoading}
      />

      <DeleteAttachment
        open={openDeleteAttachment}
        deletingAttachment={deletingAttachment}
        onCancel={() => setOpenDeleteAttachment(false)}
        onConfirm={handleDeleteAttachment}
        isLoading={isLoading}
      />
    </>
  )
}

export default EmailAttachment
